import type { ProductEntity } from '@/entities/assortment'
import type {
	SRT_Row,
	SRT_RowSelectionState,
	SRT_SortingState,
} from '@/shared/components/master-table'
import type {
	AlertModel,
	FilterRuleModel,
	PricingCampaignModel,
	ProductsQueryItemModel,
	ProductsQueryRequestModel,
} from '@/shared/generated'
import type { Updater } from '@tanstack/react-table'

import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import fastEq from 'fast-deep-equal'
import {
	eq,
	filter,
	groupBy,
	indexBy,
	isNumber,
	keys,
	map,
	pipe,
	prop,
} from 'lodash/fp'
import {
	AlertTriangleIcon,
	DownloadIcon,
	InfoIcon,
	SearchXIcon,
} from 'lucide-react'
import memoizeOne from 'memoize-one'
import * as qs from 'qs'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useDebounceValue } from 'usehooks-ts'

import {
	ANCHOR_STATUS,
	useGeneratedAnchorFamiliesQuery,
} from '@/entities/anchor'
import {
	getNodeId,
	getRowId,
	unwrapLines,
	useAssortmentListQuery,
} from '@/entities/assortment'
import {
	useBeforeGroupChange,
	useCheckOptimizationForDeleteExport,
} from '@/entities/optimization'
import { useOptimizationGroupQuery } from '@/entities/optimization-group'
import { usePricingAlertsAnnotationsQuery } from '@/entities/price-alert'
import {
	useAssignProductsMutation,
	usePricingCampaignUnassignProducts,
} from '@/entities/pricing-campaign'
import { VideoTutorials } from '@/entities/video-tutorial'

import { OptimizationGroupActions } from '@/widgets/og-actions'
import { SavedReportIndicator } from '@/widgets/saved-report-indicator'
import { ProductEastsideDrawer } from '@/widgets/sku-sidebar'

import SelectionMessagePanel from '@/shared/components/data-grid/SelectionMessagePanel'
import TextSearchField from '@/shared/components/data-grid/TextSearchField'
import { ColumnsDrawer } from '@/shared/components/datasheet/components/ColumnsDrawer'
import { MasterTable } from '@/shared/components/master-table'
import { PageNavigatorRegister } from '@/shared/components/PageNavigator'
import { Operators } from '@/shared/components/query-builder'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Badge } from '@/shared/components/ui/badge'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Footer, Header, Layout } from '@/shared/components/ui/layout'
import { Loader } from '@/shared/components/ui/loader'
import {
	PaginationControlled,
	usePagination,
} from '@/shared/components/ui/pagination-controlled'
import { AlertLevel, SavedFilterType, VideoTag } from '@/shared/generated'
import { useCanEditModelInput } from '@/shared/hooks/data'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { useSelectedItemsFilters } from '@/shared/hooks/useSelectedItemsFilters'
import { ampli } from '@/shared/lib/ampli'
import { useDatasheetTable } from '@/shared/lib/datasheet/lib/useDatasheetTable'
import { useStore } from '@/shared/lib/datasheet/store'
import { useModals } from '@/shared/lib/modal-manager'
import { useProductsTableConfigStore } from '@/shared/lib/table-config'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'
import { encodeComplexQuery } from '@/shared/tools/location'

import { useColumns } from '../lib/useColumns'
import { useListenSocket } from '../lib/useListenSocket'
import OptimizationGroupPricngAlertsButton from './OptimizationGroupPricngAlertsButton'
import ProductsToolbar from './ProductsToolbar'

export default function ProductsPage() {
	const { t } = useTranslation()

	useListenSocket()

	const { showModal } = useModals()
	const schema = useStore((state) => state.schema)
	const optimizationGroupId = useOptimizationGroupId()!
	const { data: optimizationGroup } =
		useOptimizationGroupQuery(optimizationGroupId)
	const { onMutate } = useBeforeGroupChange(optimizationGroupId)
	const checkExport = useCheckOptimizationForDeleteExport(optimizationGroupId)
	const canEditModelInput = useCanEditModelInput(optimizationGroupId)
	const [{ limit, offset, search: searchText, filters, sorting }, setQuery] =
		useLocationQuery()

	const rowSelection = useStore((state) => state.rowSelection)
	const setRowSelection = useStore((state) => state.setRowSelection)
	const isAllProductsSelected = useStore((state) => state.isAllProductsSelected)
	const skuPanelContent = useStore((state) => state.sidebarContent)

	const setIsAllProductsSelected = useStore(
		(state) => state.setIsAllProductsSelected
	)
	const { data: alerts } = usePricingAlertsAnnotationsQuery({
		select: indexBy<AlertModel>('id'),
	})

	const [searchTextValue] = useDebounceValue(searchText, 1000)
	useEffect(() => {
		setQuery({ offset: 0 })
	}, [filters, searchTextValue])

	useEffect(() => {
		handleClearSelected()
	}, [limit, offset, searchTextValue, filters, sorting])

	const requestBody: ProductsQueryRequestModel = {
		limit,
		offset,
		filters: [
			{
				name: 'optimization_group_id',
				operation: Operators.IS,
				value: optimizationGroupId,
			},
			...filters,
		],
		search: searchTextValue || undefined,
		order_by:
			sorting.map(({ id, desc }) => ({
				name: id,
				direction: desc ? 'desc' : 'asc',
			})) ?? [],
	}
	const assortmentQuery = useAssortmentListQuery(requestBody, {
		placeholderData: (previousData) => previousData,
	})
	const assignProducts = useAssignProductsMutation({
		onMutate,
	})
	const unassignProducts = usePricingCampaignUnassignProducts({
		onMutate,
	})
	const pagination = usePagination({
		total: assortmentQuery.data?.total_count ?? 0,
		limit: Number(requestBody.limit),
		offset: Number(requestBody.offset),
		onChange: (props) => {
			if (isAllProductsSelected) {
				setRowSelection({})
				setIsAllProductsSelected(false)
			}

			return setQuery(props)
		},
	})
	const data = assortmentQuery.data?.data || []
	const subRows = useMemo(
		() =>
			groupBy(
				getNodeId,
				linesToProducts(assortmentQuery.data?.data || [])
			) as Record<string, ProductEntity[]>,
		[assortmentQuery.data?.data]
	)
	const skus = useMemo(
		() => getSkus(assortmentQuery.data?.data || []),
		[assortmentQuery.data?.data]
	)
	const getChildTreeItems = (row: ProductsQueryItemModel): ProductEntity[] =>
		subRows[getNodeId(row)] || undefined
	const getSelectedFilters = useSelectedItemsFilters()
	const doIfCanEdit = useCallback(
		(fn: any) =>
			(...args: any[]) =>
				canEditModelInput
					? fn(...args)
					: toast(t('model_input_change_denied'), {
							icon: <AlertTriangleIcon className='text-warning' />,
						}),
		[canEditModelInput, t]
	)
	const handleOpenBulkModal = () =>
		showModal('UPDATE_PRODUCTS', {
			filters: getSelectedFilters(filters),
		})
	const handleClearSelected = () => {
		setRowSelection({})
		setIsAllProductsSelected(false)
	}
	const handleAllProductsSelection = () => {
		setIsAllProductsSelected(true)
		setRowSelection(
			getSkus(assortmentQuery.data?.data || []).reduce((acc, sku) => {
				acc[sku] = true

				return acc
			}, {} as SRT_RowSelectionState)
		)
	}
	const handleOpenImportModal = () => showModal('IMPORT_PRODUCTS')
	const handleOpenExportModal = () =>
		showModal('EXPORT_ASSORTMENT', {
			requireCallback: true,
			context: requestBody,
			tableConfig: useProductsTableConfigStore.getState(),
			defaultName: `Products ${optimizationGroup?.name || ''}`,
		})
	const handleCreatePricingCampaign = useCallback(
		({
			draft,
		}: {
			optimizationGroupId?: string | null
			scenarioId?: string
			draft?: {
				name?: string
				engine?: string
				assignment_filters?: FilterRuleModel[]
			}
		}) => {
			showModal('PRICING_CAMPAIGN_MODAL', {
				optimizationGroupId,
				draft: {
					...(draft || {}),
					assignment_filters: getSelectedFilters(filters),
				},
			})
		},
		[getSelectedFilters, showModal, filters, optimizationGroupId]
	)
	const handleAssignToPricingCampaign = async (
		campaign: PricingCampaignModel | string
	) => {
		if (campaign === '') {
			const answer = await confirm({
				title: t('confirm.unassign_pricing_campaigns.title'),
				description: t('confirm.unassign_pricing_campaigns.subtitle'),
			})
			if (answer) {
				ampli.tableUnassignFromPricingCampaigns()
				await unassignProducts.mutateAsync({
					filters: getSelectedFilters(filters),
				})
				setRowSelection({})
			}
		} else if (typeof campaign !== 'string') {
			const answer = await confirm({
				title: t('confirm.assign_to.static.title'),
				description: t('confirm.assign_to.static.subtitle'),
			})
			if (answer) {
				ampli.tableAssignToPricingCampaign({
					'Assign to': 'Existing',
				})
				await assignProducts.mutateAsync({
					pricingCampaignId: campaign.id,
					data: {
						filters: getSelectedFilters(filters),
					},
				})
				setRowSelection({})
			}
		}
	}
	const showSelectAllMessage = useMemo(
		() =>
			!!skus.length &&
			skus.every((id) => rowSelection[id]) &&
			skus.length >= (requestBody?.limit || 0),
		[rowSelection, skus, requestBody?.limit]
	)

	const selectedRow = skuPanelContent && getRowId(skuPanelContent)
	const getRowLabel = useCallback(
		(row: SRT_Row<ProductsQueryItemModel | ProductEntity>) => {
			const pricingAlertsAnnotations = (
				row.original as ProductsQueryItemModel
			).fields?.alerts?.map((id: string) => alerts?.[id]?.level)

			return cn({
				'row-label-success': pricingAlertsAnnotations?.some(
					eq(AlertLevel.Unassigned)
				),
				'row-label-warning': pricingAlertsAnnotations?.some(
					eq(AlertLevel.Warning)
				),
				'row-label-error': pricingAlertsAnnotations?.some(
					eq(AlertLevel.Critical)
				),
			})
		},
		[alerts]
	)
	const getRowClassName = useCallback(
		({ row }: { row: SRT_Row<ProductsQueryItemModel | ProductEntity> }) =>
			cn(
				getRowLabel(row),
				selectedRow && getRowId(row.original) === selectedRow
					? 'bg-primary-50 dark:bg-primary-950'
					: ''
			),
		[selectedRow, getRowLabel]
	)
	const { columns, dataChoices } = useColumns(schema || [])
	const table = useDatasheetTable<ProductsQueryItemModel | ProductEntity>({
		enableExpandAll: true,
		enableExpanding: true,
		enableRowSelection: true,
		enableSelectAll: true,
		enableMultiRowSelection: true,
		selectAllMode: 'tree',
		enableColumnOrdering: true,
		searchText: searchTextValue,
		data,
		columns: columns || [],
		sorting,
		onSortingChange: (updater: Updater<SRT_SortingState>) => {
			setQuery({
				sorting: (typeof updater === 'function'
					? updater(sorting)
					: updater) as SRT_SortingState,
			})
		},
		getRowClassName,
		isLoading: assortmentQuery.isFetching,
		getRowId: (row, index) => {
			if ('srt-row-spacer' in row) return `_${index}`

			return getRowId(row)
		},
		getSubRows: (row: ProductsQueryItemModel | ProductEntity) => {
			if ('srt-row-spacer' in row || 'sku' in row) return undefined

			return getChildTreeItems(row)
		},
		onSaveConfig: (id, config) => useProductsTableConfigStore.setState(config),
	})

	return (
		<Layout className='relative h-full overflow-hidden'>
			<PageNavigatorRegister>
				<div className='flex items-center gap-1'>
					<OptimizationGroupActions requestBody={requestBody} />
					<VideoTutorials tags={[VideoTag.OptimizationGroup]}>
						<Button size='icon' className='ml-auto' variant='ghost'>
							<InfoIcon />
						</Button>
					</VideoTutorials>
				</div>
			</PageNavigatorRegister>
			<div className='flex size-full flex-col overflow-hidden px-4 fade-in'>
				{assortmentQuery.isError && (
					<Alert variant='destructive' className='mt-2'>
						<AlertDescription>{t('assortment.table.error')}</AlertDescription>
					</Alert>
				)}
				<Blanket
					data-testid={
						assortmentQuery.isFetching ? 'blanket-visible' : 'blanket-hidden'
					}
					className='absolute flex flex-col items-center justify-center space-y-5 rounded-lg bg-transparent'
					isTinted={assortmentQuery.isFetching}
				>
					<div className='z-50 rounded-lg bg-white/50 p-5 shadow backdrop-blur-md fade-in dark:bg-black/50'>
						<Loader />
					</div>
				</Blanket>
				<Header className='flex w-full shrink-0 items-center gap-1 py-2'>
					<TextSearchField
						value={searchText}
						onChange={(search) => setQuery({ search })}
					/>
					<ProductsToolbar
						onOpenBulkModal={doIfCanEdit(handleOpenBulkModal)}
						onOpenImportModal={doIfCanEdit(handleOpenImportModal)}
						onAssignToPricingCampaign={checkExport(
							handleAssignToPricingCampaign
						)}
						onCreatePricingCampaign={handleCreatePricingCampaign}
						dataChoices={dataChoices}
					/>
					<div className='ml-auto flex items-center gap-1'>
						<GenaratedAnchorsBadge optimizationGroupId={optimizationGroupId} />
						<SavedReportIndicator
							type={SavedFilterType.Products}
							disabled={!filters?.length}
							filters={[
								...filters,
								{
									name: 'optimization_group_id',
									operation: Operators.IN,
									value: [optimizationGroupId],
								},
							]}
						/>
						<OptimizationGroupPricngAlertsButton />
						<ColumnsDrawer table={table} />
					</div>
				</Header>
				{showSelectAllMessage && (
					<SelectionMessagePanel
						selectedProducts={keys(rowSelection)?.length}
						totalProducts={assortmentQuery.data?.total_skus || 0}
						isAllSelected={isAllProductsSelected}
						onClearSelection={handleClearSelected}
						onSelectAllProducts={handleAllProductsSelection}
					/>
				)}
				<div className='border-base relative flex h-full flex-1 flex-col overflow-hidden rounded-lg border border-solid text-sm'>
					<MasterTable table={table} />
					{!data?.length && !assortmentQuery.isFetching && (
						<div className='absolute inset-0 flex items-center justify-center'>
							<Empty className='z-10 m-auto'>
								<EmptyHeader>
									<EmptyMedia variant='icon'>
										<SearchXIcon className='text-muted-foreground' />
									</EmptyMedia>
									<EmptyTitle>{t('no_data')}</EmptyTitle>
									<EmptyDescription>
										{t('app.no_data.subtitle', {
											defaultValue: 'Try to change filters or search query',
										})}
									</EmptyDescription>
								</EmptyHeader>
							</Empty>
						</div>
					)}
				</div>
				<Footer className='h-8 grow-0 items-center py-1'>
					{pagination && (
						<div className='mr-auto flex items-center gap-6'>
							<PaginationControlled
								className='m-0 w-auto'
								pagination={pagination}
							/>
							<div className='inline-flex items-center gap-1 text-xs'>
								<span>{t('app.items_on_page')}</span>:
								<strong data-testid='page-items-count'>
									{formatNumber(
										assortmentQuery.data?.data?.length || 0,
										NumberFormats.Integer
									)}
								</strong>
								<strong>{t('general_of')}</strong>
								<strong data-testid='total-items-count'>
									{formatNumber(
										assortmentQuery.data?.total_count || 0,
										NumberFormats.Integer
									)}
								</strong>
							</div>
							{!!assortmentQuery.data?.total_skus && (
								<div className='inline-flex items-center gap-1 text-xs'>
									<span>
										{t('app.skus_on_page', { defaultValue: 'SKUs on page' })}:
									</span>
									<strong data-testid='page-items-flat-count'>
										{formatNumber(skus?.length || 0, NumberFormats.Integer)}
									</strong>
									<strong>{t('general_of')}</strong>
									<strong data-testid='total-items-flat-count'>
										{formatNumber(
											assortmentQuery.data?.total_skus,
											NumberFormats.Integer
										)}
									</strong>
								</div>
							)}
						</div>
					)}
					<div className='ml-auto flex items-center space-x-2'>
						<Button
							size='sm'
							className='px-1 py-0'
							data-testid='download-table'
							onClick={handleOpenExportModal}
						>
							<DownloadIcon />
							{t('app.download_table')}
						</Button>
					</div>
				</Footer>
			</div>
			<ProductEastsideDrawer />
		</Layout>
	)
}

const GenaratedAnchorsBadge = ({
	optimizationGroupId,
}: {
	optimizationGroupId: string
}) => {
	const { t } = useTranslation()

	const navigate = useNavigate()
	const generatedAnchorFamiliesFilters = useMemo(
		() => [
			{
				name: 'optimization_group_id',
				operation: Operators.IS as const,
				value: optimizationGroupId,
			},
			{
				name: 'status',
				operation: Operators.IS as const,
				value: ANCHOR_STATUS.REVIEW,
			},
		],
		[optimizationGroupId]
	)
	const { data: totalGeneratedAnchorFamilies } =
		useGeneratedAnchorFamiliesQuery(
			{
				filters: generatedAnchorFamiliesFilters,
			},
			{
				select: prop('total'),
			}
		)
	const hasAnchorGeneratedFamiliesToApprove = useMemo(
		() =>
			isNumber(totalGeneratedAnchorFamilies) &&
			totalGeneratedAnchorFamilies > 0,
		[totalGeneratedAnchorFamilies]
	)
	const handleNavigateToAnchorFamiliesReview = () => {
		ampli.ogProductListNewAnchorsBadgeClick()
		navigate({
			to: `/p/anchor-management/anchor-approval${qs.stringify(
				{
					qf: encodeComplexQuery({
						filters: generatedAnchorFamiliesFilters,
					}),
				},
				{ addQueryPrefix: true }
			)}`,
		})
	}

	return (
		hasAnchorGeneratedFamiliesToApprove && (
			<Badge
				interactive
				variant='epic'
				size='sm'
				onClick={handleNavigateToAnchorFamiliesReview}
			>
				<span className='text-xs'>
					{formatNumber(totalGeneratedAnchorFamilies!)}
				</span>
				<span className='text-xs'>
					{t('og.new_anchors.title', { defaultValue: 'new anchors' })}
				</span>
			</Badge>
		)
	)
}

const linesToProducts = memoizeOne(unwrapLines, fastEq)
const getSkus: (data: ProductsQueryItemModel[]) => string[] = pipe([
	linesToProducts,
	map(getRowId),
])
const parseRowIds = pipe([
	map((id: string): string => id.split('__')[2]),
	filter(Boolean),
])
